// Missions (per environment, done once) and daily goals (three a day, progress summed over the day's
// runs). Pure functions over run results; the calendar day is passed in by the caller (UI layer).
import type { VehicleId } from '../config/vehicles';

/** What a finished (or ongoing) run achieved. */
export interface RunResult {
  envId: string;
  vehicle: VehicleId;
  distance: number;
  coins: number;
  flips: number;
  deliveries: number;
  bestAir: number;
}

type Measure = 'distance' | 'coins' | 'flips' | 'deliveries' | 'bestAir';

export interface Mission {
  id: string;
  envId: string;
  text: string;
  measure: Measure;
  target: number;
  /** Only counts when driving this vehicle. */
  vehicle?: VehicleId;
  reward: number;
}

const m = (envId: string, n: number, text: string, measure: Measure, target: number, reward: number, vehicle?: VehicleId): Mission => ({
  id: `${envId}:${n}`,
  envId,
  text,
  measure,
  target,
  reward,
  vehicle,
});

/** Three missions per environment, in rising difficulty. */
export const MISSIONS: readonly Mission[] = [
  m('bukit-sawah', 1, 'Deliver to the first school (500 m)', 'deliveries', 1, 40),
  m('bukit-sawah', 2, 'Land a flip', 'flips', 1, 60),
  m('bukit-sawah', 3, 'Collect 50 coins in one run', 'coins', 50, 100),
  m('kebun-teh', 1, 'Reach 600 m', 'distance', 600, 60),
  m('kebun-teh', 2, 'Stay airborne for 2 s', 'bestAir', 2, 80),
  m('kebun-teh', 3, 'Deliver to 2 schools in one run', 'deliveries', 2, 140),
  m('jalan-kampung', 1, 'Do 2 flips in one run', 'flips', 2, 80),
  m('jalan-kampung', 2, 'Reach 700 m on the Motor Antar', 'distance', 700, 120, 'motor'),
  m('jalan-kampung', 3, 'Collect 70 coins in one run', 'coins', 70, 150),
  m('pantai-bali', 1, 'Stay airborne for 2.5 s', 'bestAir', 2.5, 100),
  m('pantai-bali', 2, 'Reach 1000 m', 'distance', 1000, 150),
  m('pantai-bali', 3, 'Do 3 flips in one run', 'flips', 3, 160),
  m('kawah-bromo', 1, 'Reach 800 m in the Truk Dapur', 'distance', 800, 150, 'truck'),
  m('kawah-bromo', 2, 'Deliver to 3 schools in one run', 'deliveries', 3, 200),
  m('kawah-bromo', 3, 'Stay airborne for 3 s', 'bestAir', 3, 220),
];

export function missionsFor(envId: string): Mission[] {
  return MISSIONS.filter((x) => x.envId === envId);
}

export function missionMet(mission: Mission, run: RunResult): boolean {
  if (run.envId !== mission.envId) return false;
  if (mission.vehicle && run.vehicle !== mission.vehicle) return false;
  return run[mission.measure] >= mission.target;
}

/** Daily goals: progress adds up over every run of the day, in any environment. */
export interface DailyGoal {
  id: string;
  text: string;
  measure: 'distance' | 'coins' | 'flips' | 'deliveries' | 'runs';
  target: number;
  reward: number;
}

export const DAILY_POOL: readonly DailyGoal[] = [
  { id: 'drive-1500', text: 'Drive 1500 m', measure: 'distance', target: 1500, reward: 60 },
  { id: 'drive-3000', text: 'Drive 3000 m', measure: 'distance', target: 3000, reward: 100 },
  { id: 'coins-80', text: 'Collect 80 coins', measure: 'coins', target: 80, reward: 60 },
  { id: 'coins-150', text: 'Collect 150 coins', measure: 'coins', target: 150, reward: 100 },
  { id: 'flips-3', text: 'Land 3 flips', measure: 'flips', target: 3, reward: 70 },
  { id: 'flips-6', text: 'Land 6 flips', measure: 'flips', target: 6, reward: 110 },
  { id: 'deliver-3', text: 'Deliver to 3 schools', measure: 'deliveries', target: 3, reward: 80 },
  { id: 'runs-5', text: 'Finish 5 runs', measure: 'runs', target: 5, reward: 50 },
];
/** Paid once all three of the day's goals are done. */
export const DAILY_BONUS = 100;

/** Three goals with different measures for a day key like "2026-10-08": a deterministic pick from the pool. */
export function dailyGoalsFor(day: string): DailyGoal[] {
  let h = 2166136261;
  for (let i = 0; i < day.length; i++) h = Math.imul(h ^ day.charCodeAt(i), 16777619) >>> 0;
  const pool = [...DAILY_POOL];
  const out: DailyGoal[] = [];
  while (out.length < 3) {
    h = Math.imul(h ^ (h >>> 15), 2246822519) >>> 0;
    const goal = pool.splice(h % pool.length, 1)[0];
    // One goal per measure, so a day never asks for the same thing twice.
    if (!out.some((g) => g.measure === goal.measure)) out.push(goal);
  }
  return out;
}

export interface Progress {
  /** Mission ids completed (paid once). */
  done: string[];
  daily: { day: string; totals: Record<DailyGoal['measure'], number>; claimed: string[]; bonus: boolean };
}

const zeroTotals = (): Progress['daily']['totals'] => ({ distance: 0, coins: 0, flips: 0, deliveries: 0, runs: 0 });

export function emptyProgress(day: string): Progress {
  return { done: [], daily: { day, totals: zeroTotals(), claimed: [], bonus: false } };
}

export interface Payout {
  missions: Mission[];
  goals: DailyGoal[];
  bonus: boolean;
  coins: number;
}

/**
 * Applies a finished run: completes missions it met and adds it to today's totals, claiming goals
 * that reached their target. Pure: returns the new progress and what was paid.
 */
export function applyRun(progress: Readonly<Progress>, run: RunResult, day: string): { progress: Progress; payout: Payout } {
  const daily = progress.daily.day === day ? progress.daily : { day, totals: zeroTotals(), claimed: [], bonus: false };
  const missions = MISSIONS.filter((x) => !progress.done.includes(x.id) && missionMet(x, run));
  const totals = {
    distance: daily.totals.distance + Math.floor(run.distance),
    coins: daily.totals.coins + run.coins,
    flips: daily.totals.flips + run.flips,
    deliveries: daily.totals.deliveries + run.deliveries,
    runs: daily.totals.runs + 1,
  };
  const goals = dailyGoalsFor(day).filter((g) => !daily.claimed.includes(g.id) && totals[g.measure] >= g.target);
  const claimed = [...daily.claimed, ...goals.map((g) => g.id)];
  const bonus = !daily.bonus && claimed.length >= 3;
  const coins = missions.reduce((t, x) => t + x.reward, 0) + goals.reduce((t, g) => t + g.reward, 0) + (bonus ? DAILY_BONUS : 0);
  return {
    progress: { done: [...progress.done, ...missions.map((x) => x.id)], daily: { day, totals, claimed, bonus: daily.bonus || bonus } },
    payout: { missions, goals, bonus, coins },
  };
}

/** Coerces untrusted saved data. */
export function sanitizeProgress(raw: unknown, day: string): Progress {
  const data = (raw ?? {}) as { done?: unknown; daily?: { day?: unknown; totals?: Record<string, unknown>; claimed?: unknown; bonus?: unknown } };
  const p = emptyProgress(day);
  if (Array.isArray(data.done)) p.done = data.done.filter((id): id is string => typeof id === 'string' && MISSIONS.some((x) => x.id === id));
  const d = data.daily;
  if (d && d.day === day) {
    for (const k of Object.keys(p.daily.totals) as Array<keyof Progress['daily']['totals']>) {
      const v = d.totals?.[k];
      if (typeof v === 'number' && Number.isFinite(v) && v > 0) p.daily.totals[k] = Math.floor(v);
    }
    if (Array.isArray(d.claimed)) p.daily.claimed = d.claimed.filter((id): id is string => typeof id === 'string' && DAILY_POOL.some((g) => g.id === id));
    p.daily.bonus = d.bonus === true;
  }
  return p;
}
